import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus } from "lucide-react";

const categories = ["일상", "정치", "사회", "연예", "역사", "게임", "테크"];

const CreateDebate = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim() || !category) return;
    
    // Here you would typically send the data to your backend
    console.log("Creating debate:", { title, description, category });
    
    // Navigate back to home after creation
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" asChild>
                <Link to="/" className="flex items-center space-x-2">
                  <ArrowLeft className="w-4 h-4" />
                  <span>돌아가기</span>
                </Link>
              </Button>
              
              <div className="h-6 w-px bg-gray-300" />
              
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">B</span>
                </div>
                <span className="text-xl font-bold gradient-text">BothSides</span>
              </Link>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button variant="outline">
                로그인
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">새로운 토론 생성</h1>
          <p className="text-lg text-gray-600">
            흥미로운 주제로 사람들과 토론을 시작해보세요!
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Plus className="w-5 h-5" />
              <span>토론 주제 작성</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="category" className="block text-sm font-medium mb-2">
                  카테고리 선택
                </label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="카테고리를 선택해주세요" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label htmlFor="title" className="block text-sm font-medium mb-2">
                  토론 제목 <span className="text-red-500">*</span>
                </label>
                <Input
                  id="title"
                  type="text"
                  placeholder="예: 재택근무가 생산성에 도움이 될까?"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="text-lg"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  질문 형태로 작성하면 더 좋은 토론이 됩니다.
                </p>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium mb-2">
                  토론 설명 <span className="text-red-500">*</span>
                </label>
                <Textarea
                  id="description"
                  placeholder="토론 주제에 대한 배경 설명이나 추가 정보를 작성해주세요..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[120px]"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  명확한 설명은 더 활발한 토론을 만듭니다.
                </p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-2">토론 생성 가이드</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• 양자택일이 가능한 주제로 작성해주세요</li>
                  <li>• 개인 공격이나 혐오 표현은 금지됩니다</li>
                  <li>• 건전하고 건설적인 토론 문화를 만들어가요</li>
                </ul>
              </div>

              <div className="flex justify-end space-x-4">
                <Button type="button" variant="outline" onClick={() => navigate("/")}>
                  취소
                </Button>
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-blue-500 to-orange-500 hover:from-blue-600 hover:to-orange-600"
                  disabled={!title.trim() || !description.trim() || !category}
                >
                  토론 생성하기
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CreateDebate;